import type {
  AuthMethods,
  BackupStatus,
  DocumentMeta,
  FolderMeta,
  Library,
  SearchOptions,
  SearchResponse,
  SnapshotMeta,
  SnapshotPreview,
  User,
  WordsWritten,
  WritingGoals,
} from "@quill/shared";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code);
  }
}

/** Thrown when the request never reached the server (offline, server down). */
export class NetworkError extends Error {}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: body === undefined ? undefined : { "content-type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: "same-origin",
    });
  } catch (err) {
    throw new NetworkError(String(err));
  }
  const data = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) {
    if (res.status === 401) onUnauthorized?.();
    throw new ApiError(res.status, (data as { error?: string } | null)?.error ?? `http_${res.status}`);
  }
  return data as T;
}

let onUnauthorized: (() => void) | undefined;
export const setUnauthorizedHandler = (fn: () => void) => {
  onUnauthorized = fn;
};

export const api = {
  me: () => request<User>("GET", "/api/me"),
  authMethods: () => request<AuthMethods>("GET", "/api/auth/methods"),
  login: (username: string, password: string) => request<User>("POST", "/api/login", { username, password }),
  logout: () => request<{ ok: true }>("POST", "/api/logout"),
  library: () => request<Library>("GET", "/api/library"),
  createDocument: (title?: string, folderId?: string | null) =>
    request<DocumentMeta>("POST", "/api/documents", { title, folderId }),
  updateDocument: (id: string, patch: { title?: string; folderId?: string | null }) =>
    request<DocumentMeta>("PATCH", `/api/documents/${id}`, patch),
  trashDocument: (id: string) => request<DocumentMeta>("DELETE", `/api/documents/${id}`),
  restoreDocument: (id: string) => request<DocumentMeta>("POST", `/api/documents/${id}/restore`),
  createFolder: (name: string, parentId: string | null) => request<FolderMeta>("POST", "/api/folders", { name, parentId }),
  updateFolder: (id: string, patch: { name?: string; parentId?: string | null }) =>
    request<FolderMeta>("PATCH", `/api/folders/${id}`, patch),
  trashFolder: (id: string) => request<FolderMeta>("DELETE", `/api/folders/${id}`),
  restoreFolder: (id: string) => request<FolderMeta>("POST", `/api/folders/${id}/restore`),
  listSnapshots: (docId: string) => request<SnapshotMeta[]>("GET", `/api/documents/${docId}/snapshots`),
  saveSnapshot: (docId: string, label?: string) =>
    request<SnapshotMeta>("POST", `/api/documents/${docId}/snapshots`, { label }),
  previewSnapshot: (docId: string, id: string) =>
    request<SnapshotPreview>("GET", `/api/documents/${docId}/snapshots/${id}`),
  restoreSnapshot: (docId: string, id: string) =>
    request<{ before: SnapshotMeta }>("POST", `/api/documents/${docId}/snapshots/${id}/restore`),
  backupStatus: () => request<BackupStatus>("GET", "/api/backup"),
  backupNow: () => request<{ status: BackupStatus }>("POST", "/api/backup"),
  search: (q: string, opts: SearchOptions) =>
    request<SearchResponse>(
      "GET",
      `/api/search?${new URLSearchParams({ q, case: opts.matchCase ? "1" : "0", word: opts.wholeWord ? "1" : "0" })}`,
    ),
  dictionary: () => request<{ words: string[] }>("GET", "/api/dictionary"),
  addDictionaryWord: (word: string) => request<null>("PUT", `/api/dictionary/${encodeURIComponent(word)}`),
  removeDictionaryWord: (word: string) => request<null>("DELETE", `/api/dictionary/${encodeURIComponent(word)}`),
  goals: () => request<WritingGoals>("GET", "/api/goals"),
  setGoals: (goals: WritingGoals) => request<WritingGoals>("PUT", "/api/goals", goals),
  wordsWritten: (since: number) => request<WordsWritten>("GET", `/api/words-written?since=${Math.floor(since)}`),
  emptyTrash: () => request<{ removedDocuments: number; removedFolders: number; kept: number }>("POST", "/api/trash/empty"),
};
